// src/components/ClientsErpia.jsx — LES CLIENTS D'ERPIA, écran de l'éditeur.
//
// Les organisations à qui on vend le produit. À ne pas confondre avec le CRM,
// qui gère les entreprises chez qui un tenant place des consultants.
//
// UNE SEULE QUESTION PAR ÉCRAN : « ce client est-il vivant, et qu'est-ce qu'il
// a d'ouvert ? ». D'où trois partis pris :
//
// 1. PAS DE PASTILLE VERTE. Actif est la norme : ça ne mérite pas d'encre. Le
//    rouge du produit ne marque que l'anomalie — suspendu. L'œil cherche
//    l'exception, il ne relit pas douze fois « tout va bien ».
// 2. UNE LISTE QUI SE DÉPLIE, pas un tableau. Les données sont hétérogènes
//    (nom, code, compteurs, modules, dernière connexion) ; un tableau
//    forcerait des colonnes vides. Le dépli remplace la fiche : il n'y a pas
//    de page client ailleurs, donc rien à synchroniser.
// 3. LA FRAÎCHEUR D'USAGE EN BOUT DE LIGNE, en mono. Un client à 412
//    candidats qui ne s'est pas connecté depuis trois mois n'est pas un client
//    actif, et c'est ça qu'on vient voir.
//
// L'écran ne montre aucune donnée de client : le serveur ne lui en sert pas.
// C'est dit à l'écran plutôt que laissé à deviner.
import React, { useCallback, useEffect, useState } from 'react';
import APIService from '../services/api';

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });

/** « il y a 3 mois », « jamais » — la question de l'écran, en toutes lettres. */
const fraicheur = (iso) => {
  if (!iso) return { texte: 'jamais connecté', froid: true };
  const j = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (Number.isNaN(j)) return { texte: '—', froid: false };
  if (j <= 0) return { texte: "aujourd'hui", froid: false };
  if (j === 1) return { texte: 'hier', froid: false };
  if (j < 30) return { texte: `il y a ${j} jours`, froid: false };
  const m = Math.floor(j / 30);
  return { texte: `il y a ${m} mois`, froid: m >= 2 };
};

export default function ClientsErpia({ showNotification }) {
  const [vue, setVue] = useState(null);      // null = chargement | { clients } | { erreur }
  const [ouvert, setOuvert] = useState(null); // id du client déplié
  const [modules, setModules] = useState({}); // id -> { modules[] } | 'chargement'
  const [creation, setCreation] = useState(null);
  const [busy, setBusy] = useState(null);

  const charger = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/editeur/clients`, { headers: authH() });
      if ([403, 404, 501].includes(r.status)) { setVue({ interdit: r.status === 403 }); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setVue(j);
    } catch (e) { setVue({ erreur: e.message }); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const chargerModules = async (id) => {
    setModules(m => ({ ...m, [id]: 'chargement' }));
    try {
      const r = await fetch(`${BASE}/editeur/clients/${id}/modules`, { headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setModules(m => ({ ...m, [id]: j.modules || [] }));
    } catch (e) {
      setModules(m => ({ ...m, [id]: { erreur: e.message } }));
    }
  };

  const deplier = (id) => {
    const suivant = ouvert === id ? null : id;
    setOuvert(suivant);
    if (suivant != null && !modules[suivant]) chargerModules(suivant);
  };

  const basculerModule = async (id, cle, actif) => {
    setBusy(`${id}:${cle}`);
    try {
      const r = await fetch(`${BASE}/editeur/clients/${id}/modules`, {
        method: 'PUT', headers: jsonH(), body: JSON.stringify({ modules: { [cle]: actif } }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setModules(m => ({ ...m, [id]: j.modules }));
      await charger();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(null); }
  };

  const basculerActif = async (c) => {
    const suspendre = c.actif;
    if (suspendre && !window.confirm(
      `Suspendre « ${c.nom} » ?\n\nSes utilisateurs seront déconnectés et ne pourront plus se connecter. Rien n'est effacé : la réactivation rend l'accès tel quel.`)) return;
    setBusy(`actif:${c.id}`);
    try {
      const r = await fetch(`${BASE}/editeur/clients/${c.id}`, {
        method: 'PATCH', headers: jsonH(), body: JSON.stringify({ actif: !c.actif }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      // Le message du serveur, mot pour mot : c'est lui qui dit combien de
      // personnes viennent d'être déconnectées.
      showNotification?.(j.message, 'success');
      await charger();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(null); }
  };

  const creer = async () => {
    setBusy('creation');
    try {
      const r = await fetch(`${BASE}/editeur/clients`, {
        method: 'POST', headers: jsonH(), body: JSON.stringify(creation),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      // Le mot de passe provisoire n'existe QU'ICI. Un toast qui s'efface au
      // bout de trois secondes le perdrait : il reste à l'écran jusqu'à ce
      // qu'on ferme le panneau.
      setCreation({ fait: j.data });
      await charger();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setBusy(null); }
  };

  const ch = { width: '100%', boxSizing: 'border-box', marginTop: '5px' };
  const labelStyle = { display: 'block', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.06em',
    textTransform: 'uppercase', color: 'var(--erp-text-tertiary)' };

  return (
    <div style={{ padding: '4px 0 40px', fontFamily: 'var(--erp-font-sans)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', marginBottom: '4px' }}>
        <h1 style={{ margin: 0, fontSize: '26px', fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--erp-ink-strong)' }}>
          Clients<span style={{ color: 'var(--erp-accent)' }}>.</span>
        </h1>
        <span style={{ flex: 1 }} />
        {!creation && (
          <button type="button" className="erp-btn erp-btn--primary" onClick={() => setCreation({ nom: '', slug: '', admin_username: '', admin_email: '' })}>
            <BI icon="plus-lg" /> Nouveau client
          </button>
        )}
      </div>
      {vue?.note && (
        <p style={{ margin: '0 0 18px', fontSize: '12px', lineHeight: 1.55, color: 'var(--erp-text-tertiary)', maxWidth: '58ch' }}>
          {vue.note}
        </p>
      )}

      {vue == null && <p style={{ fontSize: '13px', color: 'var(--erp-text-tertiary)' }}><BI icon="arrow-repeat" /> Chargement…</p>}
      {vue?.interdit && <p style={{ fontSize: '13px', color: 'var(--erp-text-secondary)' }}>Cet écran est réservé à l'éditeur du produit.</p>}
      {vue?.erreur && <p style={{ fontSize: '13px', color: 'var(--erp-danger)' }}>{vue.erreur}</p>}

      {/* ── CRÉER : un dépli en tête de liste, pas une modale — même
             grammaire que le reste de l'écran. ── */}
      {creation && !creation.fait && (
        <div className="erp-card" style={{ padding: '16px', marginBottom: '14px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--erp-ink-strong)', marginBottom: '11px' }}>Nouveau client</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '12px' }}>
            <label style={labelStyle}>Nom du client
              <input className="erp-field" style={ch} value={creation.nom} autoFocus
                onChange={e => setCreation(c => ({ ...c, nom: e.target.value }))} placeholder="Cabinet Martin" />
            </label>
            <label style={labelStyle}>Code client
              <input className="erp-field" style={{ ...ch, fontFamily: 'var(--erp-font-mono)' }} value={creation.slug}
                onChange={e => setCreation(c => ({ ...c, slug: e.target.value.toLowerCase() }))} placeholder="martin" />
            </label>
            <label style={labelStyle}>Administrateur
              <input className="erp-field" style={ch} value={creation.admin_username}
                onChange={e => setCreation(c => ({ ...c, admin_username: e.target.value }))} placeholder="martin-admin" />
            </label>
            <label style={labelStyle}>Son email
              <input className="erp-field" type="email" style={ch} value={creation.admin_email}
                onChange={e => setCreation(c => ({ ...c, admin_email: e.target.value }))} placeholder="admin@cabinet-martin.ch" />
            </label>
          </div>
          <p style={{ margin: '11px 0 0', fontSize: '11.5px', lineHeight: 1.55, color: 'var(--erp-text-tertiary)' }}>
            Le code client sert d'adresse à la connexion : minuscules, chiffres et tirets. Le premier
            administrateur est créé avec le client — son nom d'utilisateur doit être libre sur tout l'ERP.
          </p>
          <div style={{ display: 'flex', gap: '8px', marginTop: '13px' }}>
            <button type="button" className="erp-btn erp-btn--primary" disabled={busy === 'creation' || !creation.nom || !creation.slug || !creation.admin_username}
              onClick={creer}>
              {busy === 'creation' ? <><BI icon="arrow-repeat" /> Création…</> : 'Créer le client'}
            </button>
            <button type="button" className="erp-btn erp-btn--secondary" onClick={() => setCreation(null)}>Annuler</button>
          </div>
        </div>
      )}

      {/* Le mot de passe provisoire n'apparaît qu'une fois : il RESTE à
          l'écran, il ne s'efface pas tout seul. */}
      {creation?.fait && (
        <div className="erp-card" style={{ padding: '16px', marginBottom: '14px', borderColor: 'var(--erp-accent)' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--erp-ink-strong)' }}>
            « {creation.fait.nom} » est créé
          </div>
          <p style={{ margin: '6px 0 12px', fontSize: '12.5px', lineHeight: 1.6, color: 'var(--erp-text-secondary)' }}>
            Transmettez ces accès maintenant : <b>le mot de passe n'est relisible nulle part ensuite</b>.
          </p>
          <div style={{ display: 'flex', gap: '22px', flexWrap: 'wrap', fontFamily: 'var(--erp-font-mono)', fontSize: '13px' }}>
            <span><span style={labelStyle}>Code client</span>{creation.fait.slug}</span>
            <span><span style={labelStyle}>Identifiant</span>{creation.fait.utilisateur?.username}</span>
            <span><span style={labelStyle}>Mot de passe</span>{creation.fait.mot_de_passe_provisoire}</span>
          </div>
          {creation.fait.note && (
            <p style={{ margin: '12px 0 0', fontSize: '11.5px', color: 'var(--erp-text-tertiary)' }}>{creation.fait.note}</p>
          )}
          <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" style={{ marginTop: '13px' }}
            onClick={() => setCreation(null)}>J'ai transmis les accès</button>
        </div>
      )}

      {/* ── LA LISTE ── */}
      {vue?.clients?.map(c => {
        const f = fraicheur(c.derniere_connexion);
        const estOuvert = ouvert === c.id;
        const mods = modules[c.id];
        return (
          <div key={c.id} className="erp-card" style={{ padding: 0, marginBottom: '8px',
            borderColor: c.actif ? undefined : 'var(--erp-danger)' }}>
            <button type="button" onClick={() => deplier(c.id)}
              style={{ display: 'flex', alignItems: 'center', gap: '13px', width: '100%', padding: '13px 16px',
                background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', font: 'inherit' }}>
              <span style={{ flex: '1 1 220px', minWidth: 0 }}>
                <span style={{ display: 'flex', alignItems: 'baseline', gap: '9px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: 'var(--erp-ink-strong)' }}>{c.nom}</span>
                  <span style={{ fontFamily: 'var(--erp-font-mono)', fontSize: '11px', color: 'var(--erp-text-tertiary)' }}>{c.slug}</span>
                  {/* Pas de pastille « actif » : c'est la norme. Seule
                      l'anomalie prend de l'encre. */}
                  {!c.actif && (
                    <span className="erp-chip" style={{ background: 'var(--erp-danger-soft)', color: 'var(--erp-danger)', fontWeight: 700 }}>Suspendu</span>
                  )}
                  {c.sandbox && (
                    <span className="erp-chip" title="Copie de données — rien ne peut en sortir">Bac à sable</span>
                  )}
                </span>
                <span style={{ display: 'block', marginTop: '3px', fontSize: '11.5px', color: 'var(--erp-text-tertiary)' }}>
                  {(vue.compteurs || []).map(k => `${c.compteurs?.[k.cle] ?? 0} ${k.libelle.toLowerCase()}`).join(' · ')}
                  {' · '}{c.modules_actifs} module{c.modules_actifs > 1 ? 's' : ''}
                </span>
              </span>
              {/* L'ÉLÉMENT SIGNATURE : la fraîcheur d'usage, en mono, en bout
                  de ligne. Un client à 412 candidats qui ne s'est pas connecté
                  depuis trois mois n'est pas un client actif. */}
              <span style={{ flex: 'none', textAlign: 'right', fontFamily: 'var(--erp-font-mono)', fontSize: '11.5px',
                color: f.froid ? 'var(--erp-danger)' : 'var(--erp-text-secondary)' }}>
                {f.texte}
                <span style={{ display: 'block', fontSize: '9.5px', letterSpacing: '0.08em',
                  textTransform: 'uppercase', color: 'var(--erp-text-tertiary)' }}>dernière connexion</span>
              </span>
              <BI icon={estOuvert ? 'chevron-up' : 'chevron-down'} style={{ flex: 'none', color: 'var(--erp-text-tertiary)' }} />
            </button>

            {estOuvert && (
              <div style={{ padding: '0 16px 15px', borderTop: '1px solid var(--erp-line)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap', margin: '13px 0' }}>
                  <button type="button" className={`erp-btn erp-btn--sm ${c.actif ? 'erp-btn--danger' : 'erp-btn--primary'}`}
                    disabled={busy === `actif:${c.id}`} onClick={() => basculerActif(c)}>
                    {c.actif ? 'Suspendre l\'accès' : 'Réactiver l\'accès'}
                  </button>
                  {c.sandbox && c.source_org_id != null && (
                    <span style={{ fontSize: '11.5px', color: 'var(--erp-text-tertiary)' }}>
                      Copie du client #{c.source_org_id}{c.clone_le ? ` du ${new Date(c.clone_le).toLocaleDateString('fr-FR')}` : ''} — rien ne peut sortir d'un bac à sable.
                    </span>
                  )}
                </div>

                <div style={labelStyle}>Modules ouverts</div>
                {mods === 'chargement' && <p style={{ fontSize: '12px', color: 'var(--erp-text-tertiary)' }}><BI icon="arrow-repeat" /> Chargement…</p>}
                {mods?.erreur && <p style={{ fontSize: '12px', color: 'var(--erp-danger)' }}>{mods.erreur}</p>}
                {Array.isArray(mods) && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '2px 18px', marginTop: '7px' }}>
                    {mods.map(m => (
                      <label key={m.cle} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '5px 0', cursor: 'pointer' }}>
                        <input type="checkbox" checked={m.actif} disabled={busy === `${c.id}:${m.cle}`}
                          onChange={e => basculerModule(c.id, m.cle, e.target.checked)}
                          style={{ marginTop: '2px', accentColor: 'var(--erp-accent)' }} />
                        <span style={{ fontSize: '12.5px', lineHeight: 1.45, color: 'var(--erp-text-secondary)' }}>
                          {m.libelle}
                          {/* Ce qui suit le défaut du produit bougera le jour
                              où le défaut change ; ce qui est posé, non. */}
                          {!m.pose && (
                            <span style={{ marginLeft: '6px', fontSize: '10.5px', color: 'var(--erp-text-tertiary)' }}>
                              suit le défaut
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {vue?.clients?.length === 0 && (
        <p style={{ fontSize: '13px', color: 'var(--erp-text-tertiary)' }}>
          Aucun client pour l'instant. « Nouveau client » crée l'organisation et son premier administrateur.
        </p>
      )}
    </div>
  );
}
